import { useMemo } from "react";
import { Coverage } from "@spotpetinsurance/spot-types/entities/Coverage";
import { PetPlan } from "./usePetPlans";

export type DefaultPlanConfig = Pick<Coverage, "details" | "level" | "type">;

export const useDefaultPlans = ({ petPlans, defaultPlanConfig }: { petPlans: Record<string, PetPlan[]> | undefined; defaultPlanConfig: DefaultPlanConfig }) => {
    const defaultPlans = useMemo(() => {
        if (!petPlans) return {};

        const result: Record<string, PetPlan | undefined> = {};

        for (const [petId, plans] of Object.entries(petPlans)) {
            // Find the plan that matches the default configuration
            const defaultPlan = plans.find(plan => {
                // Check if level matches (if specified)
                if (defaultPlanConfig.level && plan.baseCoverage.level !== defaultPlanConfig.level) {
                    return false;
                }

                // Check if coverage types match (if specified) - every configured type must be present
                if (defaultPlanConfig.type?.length) {
                    const planTypes = plan.baseCoverage.type;
                    if (!Array.isArray(planTypes) || !defaultPlanConfig.type.every(type => planTypes.includes(type))) {
                        return false;
                    }
                }

                // Check if coverage details match (if specified)
                if (defaultPlanConfig.details) {
                    const planDetails = plan.baseCoverage.details;
                    if (!planDetails) return false;

                    // Check annual limit
                    if (defaultPlanConfig.details.annualLimit?.value !== undefined && planDetails.annualLimit?.value !== defaultPlanConfig.details.annualLimit.value) {
                        return false;
                    }

                    // Check deductible
                    if (defaultPlanConfig.details.deductible?.value !== undefined && planDetails.deductible?.value !== defaultPlanConfig.details.deductible.value) {
                        return false;
                    }

                    // Check reimbursement percentage
                    if (
                        defaultPlanConfig.details.reimbursementPercent?.value !== undefined &&
                        planDetails.reimbursementPercent?.value !== defaultPlanConfig.details.reimbursementPercent.value
                    ) {
                        return false;
                    }
                }

                return true;
            });

            result[petId] = defaultPlan;
        }

        return result;
    }, [petPlans, defaultPlanConfig]);

    return defaultPlans;
};
